import {v4 as uuidv4} from 'uuid';

import type {HttpUserPrompt} from '../../client/promptParts';
import type {
  TurnPermissionsPatch,
  TurnResponse,
  TurnState,
  WbAgentClient,
} from '../../client/WbAgentClient';
import {TERMINAL_STATES} from '../../client/WbAgentClient';
import {runErrorMessage} from './errors';
import type {RuntimeEvent} from './events';
import type {AgentRuntime, ResumeTurnParams, RunTurnParams} from './runtime';
import type {StreamTurnRuntimeEventsOptions} from './stream';
import {streamTurnRuntimeEvents} from './stream';
import {
  extractTurnId,
  networkPermissionsEventForTurn,
  terminalEventForTurnState,
  terminalEventsForTurn,
  terminalMetadataEventsForTurn,
  TurnEventExtractor,
} from './turn';

export type GetParentNodeId = (nodeId: string) => string | null;

// Backend's auto-created catch-all project for turns created without scope.
// Surfaced as a real project on responses but not meaningful to label.
export const DEFAULT_PROJECT_NAME = 'wandb_agent_default_project';

const POLL_INITIAL_MS = 1000;
const POLL_SETTLED_MS = 3000;
const POLL_MAX_MS = 5000;
const POLL_FACTOR = 1.5;

/**
 * ThreadOperation holds a thread's polling `controller` and its `turnId` once
 * known, so `requestCancel` can reach the turn server-side. `cancelWake` fires
 * on cancel to expedite polling; `pendingCancelResolve` settles a cancel
 * requested before the turn id was known.
 */
type ThreadOperation = {
  controller: AbortController;
  turnId: string | null;
  cancelWake: AbortController;
  pendingCancelResolve?: (turnId: string | null) => void;
};

/**
 * Runtime backed by the wb_agent service.
 *
 * Creates turns via `POST /turns`, consumes the durable SSE stream as the live
 * path, and falls back to polling turn snapshots only when a stream closes
 * incomplete.
 *
 * Aborting a signal only stops the stream or polling; `requestCancel()` POSTs
 * the server-side cancel and leaves the operation alive so the observed
 * terminal settles the UI.
 */
export class WBAgentRuntime implements AgentRuntime {
  private operations = new Map<string, ThreadOperation>();
  private preferredAgentConfigOverridePromise?: Promise<string | null>;

  constructor(
    private readonly client: WbAgentClient,
    private readonly getParentNodeId: GetParentNodeId,
    private readonly preferredAgentConfigAlias: string | null = null
  ) {}

  /**
   * Resolve an embedding surface's preferred config once per runtime. Missing
   * aliases and lookup failures both fall back to the service default.
   */
  private preferredAgentConfigOverride(): Promise<string | null> {
    const preferredAlias = this.preferredAgentConfigAlias;
    if (preferredAlias == null) {
      return Promise.resolve(null);
    }

    this.preferredAgentConfigOverridePromise ??= this.client
      .get('/agent-aliases')
      .then(({aliases}) =>
        aliases.includes(preferredAlias) ? preferredAlias : null
      )
      .catch(() => null);
    return this.preferredAgentConfigOverridePromise;
  }

  requestCancel(
    threadId: string,
    knownTurnId?: string
  ): Promise<string | null> {
    const op = this.operations.get(threadId);
    if (!op) {
      // Nothing in flight in this runtime (e.g. a hydrated follower tab with
      // no local operation). Cancel the known turn directly if the caller
      // supplied one; otherwise the caller reverts its optimistic cancelling
      // state.
      return knownTurnId != null
        ? this.cancelTurn(knownTurnId)
        : Promise.resolve(null);
    }

    // Keep the operation alive — the stream or poll observing the server's
    // terminal is what settles the UI. Wake the poll fallback's sleep and
    // tighten its cadence so that terminal is seen promptly.
    op.cancelWake.abort();

    if (op.turnId != null) {
      return this.cancelTurn(op.turnId);
    }

    // The turn id isn't known yet; defer the POST to `setTurnId`, which fires it
    // off the createTurn response and resolves this with the POST's result.
    return new Promise<string | null>(resolve => {
      op.pendingCancelResolve = resolve;
    });
  }

  async *resumeTurn({
    turnId,
    threadId,
    signal: externalSignal,
    initialSnapshot,
  }: ResumeTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, cancelSignal, setTurnId, finish} = this.startOperation(
      threadId,
      externalSignal
    );

    // Register the resumed turn so a requestCancel can reach it.
    setTurnId(turnId);

    const runId = uuidv4();
    yield {type: 'run-started', runId, threadId, resumed: true};

    const extractor = new TurnEventExtractor(turnId);
    extractor.extract(initialSnapshot);

    try {
      if (isTerminal(initialSnapshot.state)) {
        yield* terminalEventsForTurn(initialSnapshot, runId);

        return;
      }

      yield* this.streamOrPollTurn(
        turnId,
        runId,
        signal,
        cancelSignal,
        extractor,
        {
          includeSnapshot: true,
          onSnapshot: turn => {
            const events = extractor.extract(turn);
            return [...events, ...terminalEventsForTurn(turn, runId)];
          },
          onTerminalSnapshot: turn => repairEventsForTurn(turnId, turn),
        }
      );
    } catch (err) {
      // Abort stops polling silently; surface only real errors.
      if (!signal.aborted) {
        yield {type: 'run-error', message: runErrorMessage(err), runId};
      }
    } finally {
      finish();
    }
  }

  async *runTurn({
    threadId,
    userPrompt,
    parentId,
    fallbackParentTurnId,
    agentConfigOverride,
    permissions,
    parentTurnId,
    signal: externalSignal,
  }: RunTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, cancelSignal, rekey, setTurnId, finish} =
      this.startOperation(threadId, externalSignal);

    // NOTE: Client-only; wb_agent doesn't have run IDs. This helps uniquely
    // identify conversational turns within the frontend chat state.
    const runId = uuidv4();
    yield {type: 'run-started', runId, threadId};

    try {
      const result = await this.createTurn(
        userPrompt ?? '',
        parentId ?? null,
        parentTurnId,
        fallbackParentTurnId ?? null,
        agentConfigOverride,
        permissions,
        signal
      );
      if (!result.ok) {
        return;
      }

      // Register the turn so a requestCancel can reach it. The thread id
      // rotates from the client UUID to the server thread id on promotion (in
      // the store, on this same `turn-created`); move the controller with it
      // so a later `requestCancel(threadId)` from the stop button reaches it.
      setTurnId(result.turnId);
      rekey(result.threadId);
      yield {
        type: 'turn-created',
        turnId: result.turnId,
        threadId: result.threadId,
        lastActivityAt: result.lastActivityAt,
      };

      if (signal.aborted) {
        return;
      }

      yield* this.streamOrPollTurn(
        result.turnId,
        runId,
        signal,
        cancelSignal,
        undefined,
        {
          onTerminalSnapshot: turn => repairEventsForTurn(result.turnId, turn),
        }
      );
    } catch (err) {
      // Abort stops polling silently; surface only real errors.
      if (!signal.aborted) {
        yield {type: 'run-error', message: runErrorMessage(err), runId};
      }
    } finally {
      finish();
    }
  }

  /**
   * startOperation supersedes any in-flight operation for the thread (stopping
   * its polling, not canceling its turn) and returns the handles for the new
   * one — an abort `signal`, a `cancelSignal` that expedites polling,
   * `setTurnId`/`rekey` to register the turn so `requestCancel` can reach it,
   * and `finish` to drop the operation unless a newer one replaced it.
   */
  private startOperation(
    threadId: string,
    externalSignal: AbortSignal
  ): {
    signal: AbortSignal;
    cancelSignal: AbortSignal;
    setTurnId: (turnId: string) => void;
    rekey: (newKey: string) => void;
    finish: () => void;
  } {
    this.operations.get(threadId)?.controller.abort();

    const controller = new AbortController();
    const op: ThreadOperation = {
      controller,
      turnId: null,
      cancelWake: new AbortController(),
    };

    // Keyed by the caller's thread id, which for a new chat is a client UUID
    // that rotates to the server thread id on promotion; `rekey` keeps the key
    // current.
    let currentKey = threadId;
    this.operations.set(currentKey, op);

    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    }

    return {
      signal: controller.signal,
      cancelSignal: op.cancelWake.signal,
      setTurnId: (turnId: string) => {
        op.turnId = turnId;
        // Fire a deferred cancel off the createTurn response and resolve the
        // requestCancel promise with the POST's result. Null the resolver
        // synchronously so `finish` can't also settle it.
        const resolve = op.pendingCancelResolve;
        if (resolve) {
          op.pendingCancelResolve = undefined;
          void this.cancelTurn(turnId).then(resolve);
        }
      },
      rekey: (newKey: string) => {
        // No-op when the key is unchanged (a follow-up turn's thread id already
        // equals the thread id), or when the operation already wound down.
        if (newKey === currentKey || this.operations.get(currentKey) !== op) {
          return;
        }

        this.operations.delete(currentKey);
        this.operations.set(newKey, op);

        currentKey = newKey;
      },
      finish: () => {
        // Resolve any deferred cancel that never got a turn id (createTurn
        // aborted/failed, or a newer run superseded this one) so the caller's
        // await can't hang.
        resolvePendingCancel(op, null);
        if (this.operations.get(currentKey) === op) {
          this.operations.delete(currentKey);
        }
      },
    };
  }

  private async createTurn(
    userPrompt: HttpUserPrompt,
    parentId: string | null,
    explicitParentTurnId: string | null | undefined,
    fallbackParentTurnId: string | null,
    agentConfigOverride: string | undefined,
    permissions: TurnPermissionsPatch | undefined,
    signal: AbortSignal
  ): Promise<
    | {ok: true; turnId: string; threadId: string; lastActivityAt: number}
    | {ok: false}
  > {
    const parentTurnId =
      explicitParentTurnId !== undefined
        ? explicitParentTurnId
        : (this.resolveParentTurnId(parentId) ?? fallbackParentTurnId);

    if (signal.aborted) {
      return {ok: false};
    }

    // An explicit per-turn selection wins. Otherwise, use the embedding
    // surface's preferred alias when the service advertises it.
    const effectiveAgentConfigOverride =
      agentConfigOverride ?? (await this.preferredAgentConfigOverride());

    const turnResponse = await this.client.post(
      '/turns',
      {
        // entity/project only scope the root turn; child turns inherit from
        // the root, so omit them when a parent_turn_id is set.
        ...(parentTurnId == null
          ? {entity: this.client.entity, project: this.client.project}
          : {}),
        parent_turn_id: parentTurnId,
        user_prompt: userPrompt ?? '',
        // Omit when unset so the backend uses the service default config.
        ...(effectiveAgentConfigOverride == null
          ? {}
          : {agent_config_override: effectiveAgentConfigOverride}),
        // Omit when unset so the backend inherits the parent's permissions.
        ...(permissions == null ? {} : {permissions}),
      },
      undefined,
      // Creating the turn is deliberately not abortable once started. We need
      // the backend id for promotion, cross-tab sync, and any pending cancel.
      undefined
    );

    return {
      ok: true,
      threadId: turnResponse.thread_id,
      turnId: turnResponse.id,
      // The new turn's created_at is the thread's latest activity timestamp.
      lastActivityAt: Date.parse(turnResponse.created_at),
    };
  }

  /**
   * Polls `GET /turns/{id}` with adaptive backoff until a terminal state is
   * reached. Yields content events and terminal events. On abort, returns
   * silently; polling stops and the turn is left running server-side.
   */
  private async *pollTurn(
    turnId: string,
    runId: string,
    signal: AbortSignal,
    cancelSignal: AbortSignal,
    extractor?: TurnEventExtractor
  ): AsyncGenerator<RuntimeEvent, void, undefined> {
    extractor ??= new TurnEventExtractor(turnId, {
      // NOTE: User messages are optimistically added to thread state, so drop
      // them for in-progress turns.
      skipUserMessages: true,
    });

    let hasReceivedData = false;
    let interval = POLL_INITIAL_MS;
    let lastSnapshotKey = '';
    let permissionsEmitted = false;

    while (true) {
      if (signal.aborted) {
        break;
      }

      // A pending cancel wakes this sleep once so the fast cadence hits the
      // next poll, not after the current backoff drains. Stop passing the
      // signal once it's aborted, or every later sleep would return instantly.
      await sleep(
        interval,
        signal,
        cancelSignal.aborted ? undefined : cancelSignal
      );
      if (signal.aborted) {
        break;
      }

      const turn = await this.client.get<TurnResponse>(
        `/turns/${turnId}`,
        undefined,
        signal
      );

      const snapshotKey = `${turn.state}:${(turn.messages ?? []).length}:${(turn.tool_calls ?? []).length}`;
      if (snapshotKey !== lastSnapshotKey) {
        lastSnapshotKey = snapshotKey;

        if (
          (turn.messages?.length ?? 0) > 0 ||
          (turn.tool_calls?.length ?? 0) > 0
        ) {
          hasReceivedData = true;
        }

        interval = hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS;
      } else {
        interval = Math.max(
          Math.min(interval * POLL_FACTOR, POLL_MAX_MS),
          hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS
        );
      }

      // While a cancel is pending, poll at the fast cadence so the server's
      // terminal state is observed promptly. No timeout or abort is used.
      if (cancelSignal.aborted) {
        interval = POLL_INITIAL_MS;
      }

      const events = extractor.extract(turn);
      for (const event of events) {
        yield event;
      }

      // Surface the turn's resolved network allowlist as soon as the turn is
      // observed (it's set at turn creation, not at completion), so the banner
      // shows while the turn is still in progress. Emitted once per turn.
      if (!permissionsEmitted) {
        permissionsEmitted = true;
        yield networkPermissionsEventForTurn(turn);
      }

      const terminalEvent = terminalEventForTurnState(turn.state, runId);
      if (terminalEvent) {
        for (const metadataEvent of terminalMetadataEventsForTurn(turn)) {
          yield metadataEvent;
        }
        yield terminalEvent;
        return;
      }
    }
  }

  private async *streamOrPollTurn(
    turnId: string,
    runId: string,
    signal: AbortSignal,
    cancelSignal: AbortSignal,
    extractor?: TurnEventExtractor,
    streamOpts?: StreamTurnRuntimeEventsOptions
  ): AsyncGenerator<RuntimeEvent, void, undefined> {
    try {
      yield* streamTurnRuntimeEvents(
        this.client,
        turnId,
        runId,
        signal,
        streamOpts
      );
    } catch {
      if (signal.aborted) {
        return;
      }
      const repairExtractor = new TurnEventExtractor(turnId, {
        skipUserMessages: true,
        snapshotEventMode: 'repair',
      });
      yield* this.pollTurn(
        turnId,
        runId,
        signal,
        cancelSignal,
        repairExtractor
      );
    }
  }

  /**
   * resolveParentTurnId finds the WB agent turn ID that should be the parent of
   * a new turn anchored at `messageId`. Walks up via `getParentNodeId` until it
   * finds a node whose ID encodes a turn ID (format `${turnId}:<role>`).
   * Returns `null` if no parent turn can be found for the message; callers
   * typically fall back to the thread's latest known turn id so a continuation turn
   * still parents under the conversation (e.g. when the previous turn was
   * cancelled before any extractable nodes were created).
   *
   * NOTE: This function exists because optimistic user message nodes must use
   * internal IDs, not turn-encoded IDs; a turn doesn't exist for the user
   * message until the backend creates it and returns it.
   */
  private resolveParentTurnId(messageId: string | null): string | null {
    const visited = new Set<string>();

    let current = messageId;
    while (current && !visited.has(current)) {
      const turnId = extractTurnId(current);
      if (turnId) {
        return turnId;
      }

      visited.add(current);
      current = this.getParentNodeId(current);
    }

    return null;
  }

  private async cancelTurn(turnId: string): Promise<string | null> {
    try {
      await this.client.post<TurnResponse>(
        `/turns/${turnId}/cancel`,
        undefined
      );
      return turnId;
    } catch {
      // The caller reverts its cancelling state so the user can retry.
      return null;
    }
  }
}

/**
 * resolvePendingCancel settles a deferred `requestCancel` promise once, then
 * clears the resolver so it can't fire twice.
 */
function resolvePendingCancel(
  op: ThreadOperation,
  turnId: string | null
): void {
  const resolve = op.pendingCancelResolve;
  if (resolve) {
    op.pendingCancelResolve = undefined;
    resolve(turnId);
  }
}

function isTerminal(state: TurnState): boolean {
  return TERMINAL_STATES.includes(state);
}

function repairEventsForTurn(
  turnId: string,
  turn: TurnResponse
): RuntimeEvent[] {
  return new TurnEventExtractor(turnId, {
    skipUserMessages: true,
    snapshotEventMode: 'repair',
  }).extract(turn);
}

/**
 * sleep resolves after `ms`, or early if `signal` aborts or the optional
 * `wakeSignal` fires. `wakeSignal` lets a pending cancel cut a long poll sleep
 * short without tearing down the operation the way `signal` would.
 */
function sleep(
  ms: number,
  signal: AbortSignal,
  wakeSignal?: AbortSignal
): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted || wakeSignal?.aborted) {
      resolve();
      return;
    }

    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      wakeSignal?.removeEventListener('abort', done);
      resolve();
    };

    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, {once: true});
    wakeSignal?.addEventListener('abort', done, {once: true});
  });
}
